/**
 * React bindings of the services the Library reads (W2-10 projects-data).
 *
 * Text goes through `services.i18n` (the app's I18n), so a language switch re-renders the page at once; settings,
 * storage health and the Library's own UI state re-render their readers live.
 */

import { useCallback, useEffect, useMemo, useState, useSyncExternalStore } from 'react';
import type { AppServices } from '../../app/registry/types';
import type { Locale, MessageRef, Unsubscribe } from '../../core/util/types';
import type { MessageKey } from '../../i18n/types';
import type { StorageHealth } from '../../persist/types';
export { useSettings as useSettingsValue } from '../../app/registry/react';

export interface LibraryText {
  readonly locale: Locale;
  readonly t: (key: MessageKey | string, params?: Readonly<Record<string, string | number>>) => string;
  readonly tr: (ref: MessageRef) => string;
}

/** Translation through `services.i18n`; a new object per locale so memoised children update. */
export function useText(services: Pick<AppServices, 'i18n'>): LibraryText {
  const { i18n } = services;
  const subscribe = useCallback((cb: () => void): Unsubscribe => i18n.subscribe(cb), [i18n]);
  const get = useCallback((): Locale => i18n.locale, [i18n]);
  const locale = useSyncExternalStore(subscribe, get, get);
  return useMemo(
    () => ({
      locale,
      t: (key, params) => i18n.t(key, params),
      tr: (ref) => i18n.tr(ref),
    }),
    [i18n, locale],
  );
}

/** Storage health: the first answer of `persist.health()`, then every `onHealth` update (null until known). */
export function useStorageHealth(services: Pick<AppServices, 'persist'>): {
  readonly health: StorageHealth | null;
  readonly failed: boolean;
  readonly refresh: () => void;
} {
  const { persist } = services;
  const [health, setHealth] = useState<StorageHealth | null>(null);
  const [failed, setFailed] = useState(false);
  const [tick, setTick] = useState(0);
  useEffect(() => {
    let alive = true;
    persist.health().then(
      (h) => {
        if (!alive) return;
        setHealth(h);
        setFailed(false);
      },
      () => {
        if (alive) setFailed(true);
      },
    );
    const off = persist.onHealth((h) => {
      setHealth(h);
      setFailed(false);
    });
    return () => {
      alive = false;
      off();
    };
  }, [persist, tick]);
  const refresh = useCallback(() => {
    setTick((n) => n + 1);
  }, []);
  return { health, failed, refresh };
}

/** "Now" for relative times ("edited 2 hours ago"), refreshed every `periodMs`. */
export function useNow(periodMs = 60_000): number {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const timer = setInterval(() => {
      setNow(Date.now());
    }, periodMs);
    return () => {
      clearInterval(timer);
    };
  }, [periodMs]);
  return now;
}

/** Object URLs of thumbnails, shared by every reader of the same Blob and revoked shortly after the last one goes. */
const objectUrls = new Map<Blob, { readonly url: string; refs: number; timer: ReturnType<typeof setTimeout> | null }>();
/** Revocation delay: survives a remount (StrictMode, list re-ordering) without flicker. */
const REVOKE_DELAY_MS = 2000;

function scheduleRevoke(blob: Blob): void {
  const entry = objectUrls.get(blob);
  if (entry === undefined || entry.refs > 0) return;
  if (entry.timer !== null) clearTimeout(entry.timer);
  entry.timer = setTimeout(() => {
    const cur = objectUrls.get(blob);
    if (cur === undefined || cur.refs > 0) return;
    objectUrls.delete(blob);
    if (typeof URL.revokeObjectURL === 'function') URL.revokeObjectURL(cur.url);
  }, REVOKE_DELAY_MS);
}

function objectUrlFor(blob: Blob): string | null {
  const known = objectUrls.get(blob);
  if (known !== undefined) return known.url;
  if (typeof URL.createObjectURL !== 'function') return null;
  let url: string;
  try {
    url = URL.createObjectURL(blob);
  } catch {
    return null;
  }
  objectUrls.set(blob, { url, refs: 0, timer: null });
  // Revoked unless a mounted reader takes it.
  scheduleRevoke(blob);
  return url;
}

/** An object URL for a Blob (thumbnails); null without a Blob or where object URLs do not exist. */
export function useObjectUrl(blob: Blob | null): string | null {
  const url = blob === null ? null : objectUrlFor(blob);
  useEffect(() => {
    if (blob === null) return undefined;
    const entry = objectUrls.get(blob);
    if (entry === undefined) return undefined;
    entry.refs++;
    if (entry.timer !== null) {
      clearTimeout(entry.timer);
      entry.timer = null;
    }
    return () => {
      entry.refs--;
      scheduleRevoke(blob);
    };
  }, [blob]);
  return url;
}

/** Progress (0..1) of the io worker task `task` while it runs, else null. `file` is the package path (CR-9). */
export interface IoProgress {
  readonly p: number;
  readonly file?: string;
}

/** Progress (0..1) of the io worker task `task` while it runs, else null. */
export function useIoProgress(services: Pick<AppServices, 'io'>, task: string): IoProgress | null {
  const { io } = services;
  const [state, setState] = useState<IoProgress | null>(null);
  useEffect(() => {
    let done: ReturnType<typeof setTimeout> | null = null;
    const off = io.on('progress', (e) => {
      if (e.task !== task) return;
      if (done !== null) clearTimeout(done);
      setState({ p: Math.max(0, Math.min(1, e.p)), ...(e.file === undefined ? {} : { file: e.file }) });
      // A finished task disappears shortly after reaching 100 %.
      done =
        e.p >= 1
          ? setTimeout(() => {
              setState(null);
            }, 1500)
          : null;
    });
    return () => {
      off();
      if (done !== null) clearTimeout(done);
    };
  }, [io, task]);
  return state;
}
